
  import { defineComponent, ref, onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import RegisteredTradesAdvancedSearch from '@/components/search/RegisteredTradesAdvancedSearch.vue';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import { StatusNotesMap } from './status-notes.enum';

  export default defineComponent({
    name: 'registered-trades-listing',
    components: {
      RegisteredTradesAdvancedSearch,
      Datatable,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();
      const statusNoteMap = StatusNotesMap;

      const getStatusNoteText = (id) => {
        return (
          Object.keys(statusNoteMap).find((key) => statusNoteMap[key] === id) ||
          ''
        );
      };

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('RegisteredTradesListing'), [
          translate('RegisteredTrades'),
        ]);
        reinitializeComponents();
      });
      await store.dispatch(Actions.GET_REGISTERED_TRADES);
      await store.dispatch(Actions.GET_REGISTERED_TRADE_TOTALS);
      const loading = ref(false);
      const tableHeader = ref([
        {
          name: translate('actions'),
          key: 'actions',
        },
        {
          name: translate('idColumn'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('transactionDate'),
          key: 'transactionDate',
          sortable: true,
        },
        {
          name: translate('description'),
          key: 'descriptionAr',
          sortable: true,
        },
        {
          name: translate('areaName'),
          key: 'areaName',
          sortable: true,
        },
        {
          name: translate('block'),
          key: 'block',
          sortable: true,
        },
        {
          name: translate('propertyType'),
          key: 'propertyTypeName',
          sortable: true,
        },
        {
          name: translate('price'),
          key: 'price',
          sortable: true,
        },
        {
          name: translate('size'),
          key: 'size',
          sortable: true,
        },
        {
          name: translate('pricePerM2'),
          key: 'pricePerM2',
          sortable: true,
        },
        {
          name: translate('notes'),
          key: 'notes',
          sortable: true,
        },
        // {
        //   name: translate('status'),
        //   key: 'status',
        //   sortable: true,
        // },
        {
          name: translate('status'),
          key: 'status',
          sortable: true,
        },
        {
          name: translate('statusNotes'),
          key: 'statusNotes',
          sortable: true,
        },
        {
          name: translate('blockCount'),
          key: 'blockCount',
          sortable: true,
        },
      ]);

      const toggleStatusRegisteredTrades = async (registeredTrade) => {
        const deletedMessage = registeredTrade.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(
            Actions.DELETE_REGISTERED_TRADE,
            registeredTrade.id
          );
          await store.dispatch(Actions.GET_REGISTERED_TRADES);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = registeredTrade.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const editRegisteredTrades = (id) => {
        router.push({ name: 'registered-trades-editing', params: { id } });
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_REGISTERED_TRADE_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_REGISTERED_TRADES);
        loading.value = false;
      };

      const onTypeChange = async (values) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_REGISTERED_TRADE_FILTER, values);
        await store.dispatch(Actions.UPDATE_REGISTERED_TRADE_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_REGISTERED_TRADES);
        await store.dispatch(Actions.GET_REGISTERED_TRADE_TOTALS);
        loading.value = false;
      };
      const onReset = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_REGISTERED_TRADE_FILTER, value);
        await store.dispatch(Actions.GET_REGISTERED_TRADES);
        await store.dispatch(Actions.GET_REGISTERED_TRADE_TOTALS);
        loading.value = false;
      };
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableData: computed(() => store.getters.registeredTradesList),
        tableHeader,
        translate,
        totalItems: computed(() => store.getters.registeredTradesListCount),
        registeredTradesTotals: computed(
          () => store.getters.getRegisteredTradesTotals
        ),
        pageChanged,
        editRegisteredTrades,
        toggleStatusRegisteredTrades,
        goTo,
        loading,
        can,
        onTypeChange,
        onReset,
        statusNoteMap,
        getStatusNoteText,
      };
    },
  });
